import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useNotification } from '../../../context/NotificationContext';
import { loginWithGoogle } from '../../../services/authService';
// import { API_URL } from '../../../constants'; // If available, but we can rely on axios base config if set

// Temporary URL configuration or use existing env
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface OnboardingUser {
    first_name: string;
    last_name: string;
    email: string;
}

export const useOnboarding = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const verifyEmailCode = searchParams.get('verify_email');
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<OnboardingUser | null>(null);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('Enlace inválido o sin token.');
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${API_URL}/api/auth/validate-onboarding-token`, {
                    params: { token }
                });

                if (response.data.valid) {

                    localStorage.removeItem('token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user');
                    window.dispatchEvent(new Event('auth:logout'));

                    setUser(response.data.user);
                    setFirstName(response.data.user.first_name || '');
                    setLastName(response.data.user.last_name || '');
                    setEmail(response.data.user.email || '');
                } else {
                    setError('Enlace inválido o expirado.');
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Error al validar el enlace.');
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token]);

    const submitSetup = async () => {
        if (password !== confirmPassword) {
            showNotification('Las contraseñas no coinciden.', 'error');
            return;
        }

        if (!termsAccepted) {
            showNotification('Debes aceptar los Términos y condiciones.', 'error');
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post(`${API_URL}/api/auth/setup-password`, {
                token,
                password,
                first_name: firstName,
                last_name: lastName,
                email: email,
                verify_email: verifyEmailCode
            });

            // If success, log them in
            if (response.data.token) {
                // Assuming login function handles saving the token and redirecting.
                // Depending on the exact signature of login() in AuthContext.
                // Typically you'd just set the tokens in localStorage and reload or navigate.
                localStorage.setItem('token', response.data.token);
                if (response.data.refresh_token) {
                    localStorage.setItem('refresh_token', response.data.refresh_token);
                }
                if (response.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                showNotification('Cuenta configurada exitosamente.', 'success');
                // Trigger a hard reload to let AuthProvider pick up the token or use its methods
                window.location.href = '/dashboard';
            }
        } catch (err: any) {
            showNotification(err.response?.data?.message || 'Error al configurar la cuenta.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleGoogleSetup = async () => {
        if (!token) return;

        setSubmitting(true);
        try {
            const result = await loginWithGoogle();
            if (result.error) {
                showNotification(result.message || 'Error al conectar con Google', 'error');
                setSubmitting(false);
                return;
            }

            if (!result.user || !result.accessToken) {
                showNotification('Error al conectar con Google', 'error');
                setSubmitting(false);
                return;
            }

            const googleEmail = result.user.email;

            // Validate email match
            if (user?.email && googleEmail !== user?.email) {
                const confirmed = window.confirm(`Estás a punto de vincular la cuenta ${googleEmail} a tu perfil registrado como ${user?.email}. Tus notificaciones seguirán llegando al correo original. ¿Deseas continuar?`);
                if (!confirmed) {
                    setSubmitting(false);
                    return;
                }
            }

            // Proceed with linking
            const response = await axios.post(`${API_URL}/api/auth/complete-onboarding-google`, {
                token,
                firebase_token: result.accessToken,
                verify_email: verifyEmailCode
            });

            if (response.data.token) {
                localStorage.setItem('token', response.data.token);
                if (response.data.refresh_token) {
                    localStorage.setItem('refresh_token', response.data.refresh_token);
                }
                if (response.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                showNotification('Cuenta vinculada exitosamente.', 'success');
                window.location.href = '/dashboard';
            }
        } catch (err: any) {
            showNotification(err.response?.data?.message || 'Error al vincular cuenta con Google.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return {
        loading,
        error,
        user,
        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        submitSetup,
        handleGoogleSetup,
        submitting,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        termsAccepted,
        setTermsAccepted,
        showPassword,
        setShowPassword,
        showConfirmPassword,
        setShowConfirmPassword,
        email,
        setEmail
    };
};
